import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import ThemeToggle from '../components/ThemeToggle';
import OptimizedImage from '../components/OptimizedImage';
import { ArrowRight, CheckCircle2, LayoutGrid, TrendingUp, DollarSign, Zap, BarChart3, Download, Calendar, MessageCircle, Users, Lock, Sparkles, ChevronRight, Play } from 'lucide-react';

const Landing = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: LayoutGrid,
      title: "Board Types Spécialisés",
      description: "Project, Marketing, Sales - Optimisés pour votre métier",
      color: "from-blue-500 to-blue-600",
      textColor: "text-blue-600"
    },
    {
      icon: TrendingUp,
      title: "Vues Avancées",
      description: "Gantt, Pipeline, Kanban, Calendar - Visualisez autrement",
      color: "from-purple-500 to-purple-600",
      textColor: "text-purple-600"
    },
    {
      icon: Zap,
      title: "Automations No-Code",
      description: "Workflows When→Then pour gagner du temps",
      color: "from-cyan-500 to-cyan-600",
      textColor: "text-cyan-600"
    },
    {
      icon: BarChart3,
      title: "Analytics Live",
      description: "KPIs et charts pour piloter vos performances",
      color: "from-green-500 to-green-600",
      textColor: "text-green-600"
    },
    {
      icon: Download,
      title: "Exports Pro",
      description: "PDF et CSV en 1 clic",
      color: "from-indigo-500 to-indigo-600",
      textColor: "text-indigo-600"
    },
    {
      icon: MessageCircle,
      title: "Chat Temps Réel",
      description: "Collaboration instantanée intégrée",
      color: "from-pink-500 to-pink-600",
      textColor: "text-pink-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a] overflow-hidden">
      {/* Navigation - RESPONSIVE */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <OptimizedImage 
                src="/doozum-logo.png" 
                alt="Doozum" 
                className="h-5 sm:h-6 object-contain transition-transform hover:scale-110"
                priority={true}
              />
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Theme Toggle */}
              <ThemeToggle />
              
              <Button 
                variant="ghost" 
                onClick={() => navigate('/auth?mode=login')}
                className="text-gray-700 dark:text-gray-300 hover:text-[#1C3E53] text-sm sm:text-base px-2 sm:px-4"
              >
                <span className="hidden sm:inline">Se connecter</span>
                <span className="sm:hidden">Login</span>
              </Button>
              <Button 
                className="bg-[#1C3E53] hover:bg-[#2A5470] text-white shadow-lg hover:shadow-xl transition-all text-sm sm:text-base px-3 sm:px-4"
                onClick={() => navigate('/auth')}
              >
                <span className="hidden sm:inline">Commencer</span>
                <span className="sm:hidden">Start</span>
                <ArrowRight className="ml-1 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Gradient - RESPONSIVE */}
      <section className="relative pt-20 sm:pt-28 md:pt-32 pb-12 sm:pb-16 md:pb-20 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#1C3E53] via-[#2A5470] to-[#4A90A4] opacity-5"></div>
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-40 sm:w-72 h-40 sm:h-72 bg-[#1C3E53] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute top-20 sm:top-40 right-5 sm:right-10 w-40 sm:w-72 h-40 sm:h-72 bg-[#4A90A4] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-10 sm:bottom-20 left-1/2 w-40 sm:w-72 h-40 sm:h-72 bg-[#2D7A4C] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white dark:bg-[#1a1a1a] border border-[#1C3E53]/20 dark:border-[#4A90A4]/30 rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-6 sm:mb-8 shadow-sm hover:shadow-md transition-all">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-[#1C3E53] dark:text-[#4A90A4]" />
              <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Nouvelle version disponible</span>
            </div>

            <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 leading-tight px-4">
              Gérez vos projets
              <br />
              <span className="bg-gradient-to-r from-[#1C3E53] via-[#4A90A4] to-[#2D7A4C] bg-clip-text text-transparent">
                avec intelligence
              </span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 dark:text-gray-200 mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed px-4">
              Board types spécialisés, automations visuelles, analytics temps réel.
              <br className="hidden md:block" />
              <span className="font-semibold text-[#1C3E53] dark:text-[#4A90A4]">La plateforme qui s'adapte à VOTRE métier.</span>
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 mb-8 sm:mb-12 px-4">
              <Button 
                size="lg"
                className="bg-[#1C3E53] hover:bg-[#2A5470] text-white text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-7 shadow-2xl hover:shadow-3xl transition-all transform hover:scale-105 w-full sm:w-auto"
                onClick={() => navigate('/auth')}
              >
                <Play className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                Commencer Gratuitement
              </Button>
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6 md:space-x-8 text-xs sm:text-sm text-gray-600 dark:text-gray-200 px-4">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                <span>Gratuit sans limite de temps</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                <span>Pas de carte bancaire</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                <span>Setup en 2 min</span>
              </div>
            </div>
          </div>

          {/* Preview Screenshot - RESPONSIVE */}
          <div className="mt-10 sm:mt-12 md:mt-16 max-w-6xl mx-auto px-4 sm:px-6">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#1C3E53] to-[#4A90A4] rounded-xl sm:rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
              <Card className="relative bg-white dark:bg-[#1a1a1a] p-1 sm:p-2 rounded-xl sm:rounded-2xl shadow-2xl overflow-hidden">
                <OptimizedImage 
                  src="/doozum-dashboard-preview.jpg" 
                  alt="Doozum Dashboard Interface"
                  className="w-full h-full rounded-lg sm:rounded-xl object-cover"
                  priority={true}
                  width={1200}
                  height={800}
                />
                <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 bg-white dark:bg-[#1a1a1a]/95 backdrop-blur-sm px-2 sm:px-4 py-1 sm:py-2 rounded-md sm:rounded-lg shadow-lg">
                  <p className="text-xs sm:text-sm font-semibold text-[#1C3E53] dark:text-[#4A90A4]">
                    <span className="hidden sm:inline">Interface Doozum - Dashboard & Analytics</span>
                    <span className="sm:hidden">Dashboard Doozum</span>
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - RESPONSIVE */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-b from-white to-slate-50 dark:from-[#0a0a0a] dark:to-[#111111]">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Tout pour réussir</h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-200 px-4">Des fonctionnalités pensées pour votre productivité</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className="group p-5 sm:p-6 md:p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-[#1C3E53] cursor-pointer bg-white dark:bg-[#1a1a1a]"
                >
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-200 leading-relaxed">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Board Types Showcase - MODERNIZED */}
      <section className="py-24 bg-gradient-to-b from-white via-slate-50/50 to-white dark:from-[#0a0a0a] dark:via-[#0f0f0f] dark:to-[#0a0a0a] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-blue-400/10 dark:bg-blue-500/5 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-purple-400/10 dark:bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-500/20 dark:to-purple-500/20 text-sm font-semibold text-gray-700 dark:text-gray-300 backdrop-blur-sm border border-blue-200/20 dark:border-blue-500/20">
                3 Board Types Intelligents
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-gray-100 mb-6 bg-clip-text">
              Infinies possibilités.
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Chaque board optimisé pour votre workflow, avec des vues et features spécialisées
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {/* Project */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500"></div>
              <Card className="relative bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden h-full transform group-hover:-translate-y-2 transition-all duration-500">
                {/* Glassmorphism header */}
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-blue-500/20 to-blue-600/10 dark:from-blue-500/30 dark:to-blue-600/20 backdrop-blur-xl"></div>
                
                <div className="relative p-8">
                  {/* Icon with floating animation */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-blue-500/20 dark:bg-blue-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl">
                      <LayoutGrid className="h-10 w-10 text-white" />
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Project</h3>
                    <p className="text-gray-600 dark:text-gray-300">Pilotez vos projets de A à Z</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-800/30 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Vue Gantt Timeline</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Visualisez la durée réelle</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-800/30 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Progress Tracking</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Suivi visuel 0-100%</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-blue-50/50 dark:bg-blue-900/10 border border-blue-100/50 dark:border-blue-800/30 group-hover:border-blue-300 dark:group-hover:border-blue-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Subtasks & Dépendances</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Organisation granulaire</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Marketing - Featured */}
            <div className="group relative md:scale-105">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl opacity-75 group-hover:opacity-100 blur-sm transition duration-500"></div>
              <Card className="relative bg-white dark:bg-[#1a1a1a] border-2 border-purple-200 dark:border-purple-900/50 rounded-2xl overflow-hidden h-full transform group-hover:-translate-y-2 transition-all duration-500">
                {/* Premium badge */}
                <div className="absolute top-4 right-4 z-10">
                  <span className="px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-xs font-bold text-white shadow-lg">
                    Populaire
                  </span>
                </div>

                {/* Glassmorphism header */}
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/10 dark:from-purple-500/30 dark:to-pink-500/20 backdrop-blur-xl"></div>
                
                <div className="relative p-8">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-purple-500/20 dark:bg-purple-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl">
                      <TrendingUp className="h-10 w-10 text-white" />
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Marketing</h3>
                    <p className="text-gray-600 dark:text-gray-300">Campagnes qui performent</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100/50 dark:border-purple-800/30 group-hover:border-purple-300 dark:group-hover:border-purple-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Multi-Plateformes</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Social, Email, Ads, SEO</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100/50 dark:border-purple-800/30 group-hover:border-purple-300 dark:group-hover:border-purple-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Performance Metrics</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Reach, clicks, conversions</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-purple-50/50 dark:bg-purple-900/10 border border-purple-100/50 dark:border-purple-800/30 group-hover:border-purple-300 dark:group-hover:border-purple-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Launch Date Tracking</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Ne ratez plus rien</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Sales */}
            <div className="group relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl opacity-0 group-hover:opacity-100 blur transition duration-500"></div>
              <Card className="relative bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden h-full transform group-hover:-translate-y-2 transition-all duration-500">
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-green-500/20 to-emerald-600/10 dark:from-green-500/30 dark:to-emerald-600/20 backdrop-blur-xl"></div>
                
                <div className="relative p-8">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-green-500/20 dark:bg-green-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all"></div>
                    <div className="relative w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-xl">
                      <DollarSign className="h-10 w-10 text-white" />
                    </div>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Sales</h3>
                    <p className="text-gray-600 dark:text-gray-300">Closez plus de deals</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50/50 dark:bg-green-900/10 border border-green-100/50 dark:border-green-800/30 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Pipeline Visuel</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Drag & drop entre stages</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50/50 dark:bg-green-900/10 border border-green-100/50 dark:border-green-800/30 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Deal Value & Metrics</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pipeline value, rates</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50/50 dark:bg-green-900/10 border border-green-100/50 dark:border-green-800/30 group-hover:border-green-300 dark:group-hover:border-green-600 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-600 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <CheckCircle2 className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Closing Date Alerts</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Ne manquez rien</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Power Features - MODERNIZED */}
      <section className="relative py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 dark:from-[#050505] dark:via-[#0a0a0a] dark:to-[#050505] text-white overflow-hidden">
        {/* Animated grid background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]"></div>
        </div>

        {/* Floating elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm text-sm font-semibold text-cyan-300 border border-cyan-500/20">
                Features Avancées
              </span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent">
                Automatisez. Analysez. Exportez.
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Les features qui transforment votre productivité
            </p>
          </div>
          
          {/* Cards Grid */}
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Automations Card */}
            <div className="group relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl opacity-0 group-hover:opacity-75 blur-xl transition duration-500"></div>
              
              <Card className="relative bg-gradient-to-br from-[#0f2942] to-[#1a3a52] border border-cyan-500/20 rounded-3xl overflow-hidden backdrop-blur-xl">
                {/* Animated background pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl transform translate-x-32 -translate-y-32"></div>
                
                <div className="relative p-10">
                  {/* Icon */}
                  <div className="relative inline-block mb-8">
                    <div className="absolute inset-0 bg-cyan-500/30 rounded-2xl blur-xl"></div>
                    <div className="relative w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl">
                      <Zap className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-3xl font-bold mb-3 text-white">Automations</h3>
                  <p className="text-cyan-100/80 text-lg mb-8">
                    Workflows visuels "When → Then" sans code
                  </p>

                  {/* Features list */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-cyan-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-cyan-50/90 font-medium">6 types de triggers personnalisables</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-cyan-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-cyan-50/90 font-medium">5 types d'actions automatiques</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-cyan-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-cyan-50/90 font-medium">Templates pré-configurés prêts à l'emploi</span>
                    </div>
                  </div>

                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </Card>
            </div>

            {/* Analytics Card */}
            <div className="group relative">
              {/* Glow effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl opacity-0 group-hover:opacity-75 blur-xl transition duration-500"></div>
              
              <Card className="relative bg-gradient-to-br from-[#1a3a52] to-[#2a4a62] border border-blue-500/20 rounded-3xl overflow-hidden backdrop-blur-xl">
                {/* Animated background pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent"></div>
                <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl transform -translate-x-32 -translate-y-32"></div>
                
                <div className="relative p-10">
                  {/* Icon */}
                  <div className="relative inline-block mb-8">
                    <div className="absolute inset-0 bg-blue-500/30 rounded-2xl blur-xl"></div>
                    <div className="relative w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl">
                      <BarChart3 className="h-8 w-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-3xl font-bold mb-3 text-white">Analytics</h3>
                  <p className="text-blue-100/80 text-lg mb-8">
                    Insights temps réel pour décisions éclairées
                  </p>

                  {/* Features list */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-blue-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-blue-50/90 font-medium">Charts interactifs avec Recharts</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-blue-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-blue-50/90 font-medium">KPIs spécifiques par board type</span>
                    </div>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-blue-400/30 transition-all group/item">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 group-hover/item:scale-150 transition-transform"></div>
                      <span className="text-blue-50/90 font-medium">Exports PDF & CSV professionnels</span>
                    </div>
                  </div>

                  {/* Bottom accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 bg-white dark:bg-[#1a1a1a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">Intégrations Natives</h2>
            <p className="text-xl text-gray-600 dark:text-gray-200">Connectez-vous en quelques clics</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Google */}
            <Card className="p-8 text-center hover:shadow-2xl transition-all transform hover:-translate-y-2 group">
              <div className="w-20 h-20 mx-auto mb-6 bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl transition-shadow">
                <svg className="h-12 w-12" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
                  <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
                  <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
                  <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Google OAuth</h3>
              <p className="text-gray-600 dark:text-gray-300">Connexion sécurisée en 1 clic</p>
            </Card>

            {/* GitHub */}
            <Card className="p-8 text-center hover:shadow-2xl transition-all transform hover:-translate-y-2 group">
              <div className="w-20 h-20 mx-auto mb-6 bg-gray-900 rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl transition-shadow">
                <svg className="h-12 w-12" fill="white" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">GitHub</h3>
              <p className="text-gray-600 dark:text-gray-300">Sync repos, issues & PRs</p>
            </Card>

            {/* Email */}
            <Card className="p-8 text-center hover:shadow-2xl transition-all transform hover:-translate-y-2 group">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-500 to-purple-500 rounded-2xl shadow-lg flex items-center justify-center group-hover:shadow-xl transition-shadow">
                <MessageCircle className="h-10 w-10 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Email & Chat</h3>
              <p className="text-gray-600 dark:text-gray-300">Notifications & collaboration live</p>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Doozum */}
      <section className="py-20 bg-white dark:bg-[#1a1a1a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">Pourquoi choisir Doozum ?</h2>
            <p className="text-xl text-gray-600 dark:text-gray-200">La plateforme qui fait vraiment la différence</p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            <Card className="p-8 hover:shadow-2xl transition-all group bg-gradient-to-br from-white to-blue-50/30 dark:from-[#1a1a1a] dark:to-[#111111] border-2 border-transparent hover:border-[#1C3E53] dark:border-gray-700">
              <div className="w-16 h-16 bg-gradient-to-br from-[#1C3E53] to-[#4A90A4] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">100% Sans Code</h3>
              <p className="text-gray-600 dark:text-gray-200 leading-relaxed">
                Créez des workflows complexes avec notre interface visuelle "When → Then". 
                Pas besoin de développeur, pas de formation technique. 
                <span className="font-semibold text-[#1C3E53] dark:text-[#4A90A4]"> C'est aussi simple que de faire glisser et déposer.</span>
              </p>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all group bg-gradient-to-br from-white to-purple-50/30 dark:from-[#1a1a1a] dark:to-[#111111] border-2 border-transparent hover:border-[#1C3E53] dark:border-gray-700">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LayoutGrid className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Adapté à VOTRE métier</h3>
              <p className="text-gray-600 dark:text-gray-200 leading-relaxed">
                Contrairement aux outils génériques, nos board types (Project, Marketing, Sales) 
                sont pré-configurés avec les champs et vues dont vous avez vraiment besoin. 
                <span className="font-semibold text-[#1C3E53] dark:text-[#4A90A4]"> Gagnez du temps dès le premier jour.</span>
              </p>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all group bg-gradient-to-br from-white to-green-50/30 dark:from-[#1a1a1a] dark:to-[#111111] border-2 border-transparent hover:border-[#1C3E53] dark:border-gray-700">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Insights en Temps Réel</h3>
              <p className="text-gray-600 dark:text-gray-200 leading-relaxed">
                Fini les exports Excel pour analyser vos données. 
                Visualisez vos KPIs en direct avec des charts interactifs. 
                <span className="font-semibold text-[#1C3E53] dark:text-[#4A90A4]"> Prenez des décisions éclairées instantanément.</span>
              </p>
            </Card>

            <Card className="p-8 hover:shadow-2xl transition-all group bg-gradient-to-br from-white to-cyan-50/30 dark:from-[#1a1a1a] dark:to-[#111111] border-2 border-transparent hover:border-[#1C3E53] dark:border-gray-700">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">Collaboration Intégrée</h3>
              <p className="text-gray-600 dark:text-gray-200 leading-relaxed">
                Chat en temps réel, commentaires, mentions, notifications... 
                Toute votre équipe synchronisée au même endroit. 
                <span className="font-semibold text-[#1C3E53] dark:text-[#4A90A4]"> Exit les emails interminables.</span>
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-[#0f0f0f] dark:to-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">Tarification simple et transparente</h2>
            <p className="text-xl text-gray-600 dark:text-gray-200">Un seul plan. Toutes les fonctionnalités.</p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="relative overflow-hidden border-2 border-[#1C3E53] shadow-2xl dark:border-gray-700">
              {/* Ribbon */}
              <div className="absolute top-0 right-0 bg-gradient-to-r from-[#1C3E53] to-[#4A90A4] text-white px-6 py-2 text-sm font-bold transform rotate-0">
                7 jours d'essai gratuit
              </div>

              <div className="p-12">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">Plan Pro</h3>
                  <div className="flex items-baseline justify-center space-x-2">
                    <span className="text-6xl font-bold text-[#1C3E53] dark:text-[#4A90A4]">5.99€</span>
                    <div className="text-left">
                      <div className="text-xl text-gray-600 dark:text-gray-300">/mois</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">par utilisateur</div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    'Boards et tâches illimités',
                    'Toutes les vues (Kanban, Gantt, Calendar, Pipeline)',
                    'Automations visuelles no-code',
                    'Analytics et exports PDF/CSV',
                    'Team Chat en temps réel',
                    'Intégrations GitHub et Google',
                    'Support prioritaire',
                    'Mises à jour régulières'
                  ].map((feature, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  size="lg"
                  className="w-full bg-[#1C3E53] hover:bg-[#2A5470] text-white text-lg py-6"
                  onClick={() => navigate('/auth')}
                >
                  Commencer l'essai gratuit
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>

                <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-6">
                  Aucune carte bancaire requise · Mode lecture seule après l'essai · Annulez à tout moment
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white dark:bg-[#1a1a1a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">Questions fréquentes</h2>
            <p className="text-xl text-gray-600 dark:text-gray-200">Tout ce que vous devez savoir</p>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">L'essai gratuit est-il vraiment sans engagement ?</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Oui, absolument ! Vous bénéficiez de 7 jours d'essai avec toutes les fonctionnalités, 
                sans avoir à entrer de carte bancaire. Après l'essai, votre compte passe en mode lecture seule 
                et vous conservez l'accès à vos données.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Puis-je changer de board type plus tard ?</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Oui, vous pouvez créer autant de boards que vous voulez, de n'importe quel type (Project, Marketing, Sales). 
                Chaque board est indépendant et vous pouvez les configurer selon vos besoins.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Combien d'utilisateurs puis-je inviter ?</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Vous pouvez inviter autant d'utilisateurs que vous le souhaitez. Le tarif est de 5.99€/mois par utilisateur actif. 
                Les membres en lecture seule ne sont pas facturés.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Mes données sont-elles sécurisées ?</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Nous prenons la sécurité très au sérieux. Toutes vos données sont chiffrées, 
                hébergées en Europe (conformité RGPD), et sauvegardées quotidiennement. 
                Vous pouvez exporter ou supprimer vos données à tout moment depuis vos paramètres.
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-3">Proposez-vous un support client ?</h3>
              <p className="text-gray-600 dark:text-gray-200">
                Oui ! Tous nos utilisateurs bénéficient d'un support par email. 
                Les utilisateurs du plan Pro ont accès à un support prioritaire avec un temps de réponse garanti sous 24h.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-[#0f0f0f] dark:to-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <Card className="p-12 bg-gradient-to-br from-white to-slate-50 dark:from-[#1a1a1a] dark:to-[#0f0f0f] border-2 border-[#1C3E53]/10 dark:border-gray-700">
              <div className="grid md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-5xl font-bold text-[#1C3E53] dark:text-[#4A90A4] mb-2">5</div>
                  <p className="text-gray-600 dark:text-gray-200 font-medium">Types de vues</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Table, Kanban, Calendar, Gantt, Pipeline</p>
                </div>
                <div>
                  <div className="text-5xl font-bold text-[#1C3E53] dark:text-[#4A90A4] mb-2">3</div>
                  <p className="text-gray-600 dark:text-gray-200 font-medium">Board types</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Project, Marketing, Sales</p>
                </div>
                <div>
                  <div className="text-5xl font-bold text-[#1C3E53] dark:text-[#4A90A4] mb-2">∞</div>
                  <p className="text-gray-600 dark:text-gray-200 font-medium">Possibilités</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Customisation illimitée</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Final CTA - RESPONSIVE */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-gradient-to-br from-[#1C3E53] via-[#2A5470] to-[#1C3E53] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:30px_30px]"></div>
        <div className="container mx-auto px-4 sm:px-6 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
            Prêt à booster votre productivité ?
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-blue-100 mb-6 sm:mb-8 md:mb-10 max-w-3xl mx-auto px-4">
            Rejoignez Doozum aujourd'hui et découvrez une nouvelle façon de travailler.
          </p>
          <Button 
            size="lg"
            className="bg-white dark:bg-[#1a1a1a] text-[#1C3E53] dark:text-[#4A90A4] hover:bg-slate-100 dark:hover:bg-gray-800 text-base sm:text-lg md:text-xl px-8 sm:px-10 md:px-12 py-5 sm:py-6 md:py-8 shadow-2xl hover:shadow-3xl transition-all transform hover:scale-110 w-full sm:w-auto max-w-md mx-auto"
            onClick={() => navigate('/auth')}
          >
            Commencer Gratuitement
            <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6" />
          </Button>
          <p className="text-blue-200 mt-4 sm:mt-6 text-sm sm:text-base md:text-lg px-4">
            Aucun engagement · Annulez quand vous voulez
          </p>
        </div>
      </section>

      {/* Footer - RESPONSIVE */}
      <footer className="bg-slate-900 text-white py-8 sm:py-10 md:py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-800 pt-6 sm:pt-8 space-y-4 sm:space-y-0 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <OptimizedImage 
                src="/doozum-logo.png" 
                alt="Doozum" 
                className="h-4 sm:h-5 object-contain opacity-80"
                lazy={true}
              />
              <span className="text-slate-400 text-xs sm:text-sm text-center sm:text-left">
                © 2025 Doozum. Tous droits réservés.
              </span>
            </div>
            <div className="flex items-center space-x-4 sm:space-x-6 md:space-x-8">
              <a onClick={() => navigate('/documentation')} className="text-slate-400 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer">
                <span className="hidden sm:inline">Documentation</span>
                <span className="sm:hidden">Docs</span>
              </a>
              <a onClick={() => navigate('/support')} className="text-slate-400 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer">Support</a>
              <a onClick={() => navigate('/privacy')} className="text-slate-400 hover:text-white transition-colors text-xs sm:text-sm cursor-pointer">
                <span className="hidden sm:inline">Confidentialité</span>
                <span className="sm:hidden">Privacy</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
      
      <style jsx>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Landing;
